import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { listAPI } from '../services/api';
import { GroceryList } from '../types';

export interface FamilyRef { _id: string; name: string; }

interface CurrentListValue {
  lists: GroceryList[];
  families: FamilyRef[];
  currentFamilyId: string | null;
  currentListId: string | null;
  currentFamily: FamilyRef | null;
  currentList: GroceryList | null;
  listsForCurrentFamily: GroceryList[];
  setCurrentFamilyId: (id: string) => void;
  setCurrentListId: (id: string) => void;
  reload: () => Promise<void>;
  loading: boolean;
}

const CurrentListContext = createContext<CurrentListValue | null>(null);
const LIST_KEY = 'current_list_id';
const FAMILY_KEY = 'current_family_id';

const familyIdOf = (l: GroceryList): string =>
  typeof l.family === 'string' ? l.family : l.family._id;
const familyRefOf = (l: GroceryList): FamilyRef =>
  typeof l.family === 'string' ? { _id: l.family, name: '' } : l.family;

export const CurrentListProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lists, setLists] = useState<GroceryList[]>([]);
  const [families, setFamilies] = useState<FamilyRef[]>([]);
  const [currentFamilyId, setFamilyState] = useState<string | null>(() => localStorage.getItem(FAMILY_KEY));
  const [currentListId, setListState] = useState<string | null>(() => localStorage.getItem(LIST_KEY));
  const [loading, setLoading] = useState(true);

  const setCurrentListId = useCallback((id: string) => {
    setListState(id);
    localStorage.setItem(LIST_KEY, id);
  }, []);

  // Switching family selects that family's main list (or its first list).
  const selectFamily = useCallback((famId: string, all: GroceryList[]) => {
    setFamilyState(famId);
    localStorage.setItem(FAMILY_KEY, famId);
    const famLists = all.filter((l) => familyIdOf(l) === famId);
    const target = famLists.find((l) => l.isMain) || famLists[0];
    if (target) setCurrentListId(target._id);
  }, [setCurrentListId]);

  const setCurrentFamilyId = useCallback((id: string) => selectFamily(id, lists), [lists, selectFamily]);

  const reload = useCallback(async () => {
    const res = await listAPI.getAll();
    const data = res.data || [];
    setLists(data);

    // Unique families, in first-seen order.
    const fam: FamilyRef[] = [];
    for (const l of data) {
      const f = familyRefOf(l);
      if (!fam.some((x) => x._id === f._id)) fam.push(f);
    }
    setFamilies(fam);

    // Resolve a valid family + list from storage, else defaults.
    const storedFam = localStorage.getItem(FAMILY_KEY);
    const storedList = localStorage.getItem(LIST_KEY);
    const validFam = fam.find((f) => f._id === storedFam)?._id || fam[0]?._id || null;

    if (validFam) {
      setFamilyState(validFam);
      localStorage.setItem(FAMILY_KEY, validFam);
      const famLists = data.filter((l) => familyIdOf(l) === validFam);
      const validList = famLists.find((l) => l._id === storedList)
        || famLists.find((l) => l.isMain)
        || famLists[0];
      if (validList) setCurrentListId(validList._id);
      else { setListState(null); localStorage.removeItem(LIST_KEY); }
    } else {
      setFamilyState(null); setListState(null);
      localStorage.removeItem(FAMILY_KEY); localStorage.removeItem(LIST_KEY);
    }
  }, [setCurrentListId]);

  useEffect(() => { reload().finally(() => setLoading(false)); }, [reload]);

  const currentFamily = families.find((f) => f._id === currentFamilyId) || null;
  const currentList = lists.find((l) => l._id === currentListId) || null;
  const listsForCurrentFamily = lists.filter((l) => familyIdOf(l) === currentFamilyId);

  return (
    <CurrentListContext.Provider value={{
      lists, families, currentFamilyId, currentListId, currentFamily, currentList,
      listsForCurrentFamily, setCurrentFamilyId, setCurrentListId, reload, loading,
    }}>
      {children}
    </CurrentListContext.Provider>
  );
};

export const useCurrentList = () => {
  const ctx = useContext(CurrentListContext);
  if (!ctx) throw new Error('useCurrentList must be used within CurrentListProvider');
  return ctx;
};
